import { nodePathToFriendlyName, cancelCodeInformationalArray } from '@ourbranch/lookups';
import { policyDetailsFromSegments } from '@ourbranch/policy-utils';
import up from 'core/assets/svg/up.svg';
import down from 'core/assets/svg/down.svg';
import { toCapitalized } from 'core/helpers/formatters';

export function getPriceDetails({ newTotalPrice }, total) {
  if (newTotalPrice > total) {
    return {
      arrow: up,
      label: 'increase'
    };
  }
  if (newTotalPrice < total) {
    return {
      arrow: down,
      label: 'decrease'
    };
  }
  return {
    arrow: null,
    label: 'not change'
  };
}

/**
 *
 * @param {string} changes - the node paths of the changes in a comma separated string
 * @param {array} additionalItems - user friendly strings to also be displayed as items changed to the user
 * @returns
 */
export function parseCoverageChanges(changes, additionalItems) {
  const changedNodePaths = changes.split(',');
  const noChanges = changes === 'None';

  if (noChanges && additionalItems?.length) {
    return additionalItems.join(', ');
  }

  const itemsChanged = changedNodePaths
    .map((nodePath) => nodePathToFriendlyName[nodePath] ?? toCapitalized(nodePath))
    .join(', ');

  if (additionalItems?.length) {
    return `${itemsChanged}, ${additionalItems.join(', ')}`;
  }

  return itemsChanged;
}

export function parsePreviewChanges(changes, additionalItems) {
  const changedNodePaths = changes.split(',');
  const noChanges = changes === 'None';

  if (noChanges && additionalItems?.length) {
    return additionalItems.join(', ');
  }

  const itemsChanged = changedNodePaths
    .map((nodePath) => nodePathToFriendlyName[nodePath] ?? toCapitalized(nodePath))
    .join(', ');

  if (additionalItems?.length) {
    return `${itemsChanged}, ${additionalItems.join(', ')}`;
  }

  return itemsChanged;
}

export function determineBoxTwoText(cancelReason) {
  return cancelCodeInformationalArray.find((cancelCodeObject) => cancelCodeObject.cancelCode === cancelReason)
    .boxTwoText;
}

export function canAddToInternalNote(cancelReason) {
  return !!cancelCodeInformationalArray.find((cancelCodeObject) => cancelCodeObject.cancelCode === cancelReason)
    .allowFreeFormNote;
}

export const getButtonLabel = (action) => {
  if (action === 'rescind') {
    return 'Rescind cancellation';
  }
  if (action === 'reinstate') {
    return 'Reinstate policy';
  }
  if (action === 'cancel') {
    return 'Cancel policy';
  }
  return 'Save policy changes';
};

const isValuePresentInObject = (keys, obj, value) => {
  for (const key of keys) {
    if (obj.hasOwnProperty(key) && obj[key] === value) {
      return true;
    }
  }
  return false;
};

export const getNotes = ({ action, cancelReason, nonRenewReason, itemsChanged, diff }) => {
  const cancelBoxTwoText =
    action === 'cancel' &&
    cancelReason !== 'NA' &&
    (itemsChanged?.includes('cancelReason') || itemsChanged?.includes('endDate'))
      ? determineBoxTwoText(cancelReason)
      : undefined;

  if (cancelBoxTwoText) {
    //  some pre-determined notes based on cancel reasons cannot be edited
    const disableInternalNote = cancelBoxTwoText && !canAddToInternalNote(cancelReason);
    return { notesText: cancelBoxTwoText, cancelBoxTwoText, disableInternalNote };
  }

  const nonRenewBoxTwoText =
    nonRenewReason && nonRenewReason !== 'NA' && itemsChanged?.includes('autoRenew')
      ? determineBoxTwoText(nonRenewReason)
      : undefined;

  if (nonRenewBoxTwoText) {
    return { notesText: nonRenewBoxTwoText };
  }

  // Garage address rule exception approved
  const diffObj = diff && JSON.parse(diff);
  const addedChanges = diffObj?.full?.addedChanges;
  if (addedChanges) {
    const garageAddressRuleExceptionKeys = Object.keys(addedChanges).filter((k) =>
      k.includes('isGarageAddressRuleExceptionApproved')
    );
    const approvingGarageAddressRuleException = isValuePresentInObject(
      garageAddressRuleExceptionKeys,
      addedChanges,
      true
    );
    if (approvingGarageAddressRuleException) {
      return { notesText: 'Underwriting approved garage address rule exception' };
    }
  }

  return { notesText: '' };
};

const getPipOptionIndex = (pipOption, excludedResidentsNum) => {
  const pipOptionsMap = {
    UNLIMITED: 1,
    500000: 2,
    250000: 3,
    '250000W/EXCLUDED': 4,
    50000: 5,
    OPTOUT: 6
  };

  if (excludedResidentsNum && pipOption === '250000') {
    return 4;
  }

  return pipOptionsMap[pipOption];
};

export const getChangesForApproval = ({ policy, preview, session }) => {
  if (!preview || !policy || policy?.state !== 'MI' || policy.policyType !== 'A') {
    return {};
  }
  const {
    diff,
    createdBy: { isAgency: requestedByIA }
  } = preview;
  const policyDetails = policyDetailsFromSegments(policy);
  const { canApproveRequestedPIPChanges } = session;

  const diffObj = diff && JSON.parse(diff);
  const addedChanges = diffObj?.full?.addedChanges || {};
  const removedChanges = diffObj?.full?.removedChanges || {};

  const initialPipOption = policyDetails?.autoCoverage?.policyLimitPIPME;
  const currentPipOption = addedChanges['autoCoverage.policyLimitPIPME'];
  const currentExcludedResidentsNum = addedChanges?.['auto.pipExcludedResidents'] || 0;
  const currentPipOptionIndex = getPipOptionIndex(currentPipOption, currentExcludedResidentsNum);
  const initialExcludedResidents = policyDetails.drivers
    .concat(policyDetails.nonDrivers || [])
    .some((d) => d.pipHasQHC || d.pipOptOut);

  // we used to not have option 4, and as far as rating goes, options 3 and 4 are the same, so identify when we are on option 3 with excluded so we dont show docs when we silently move them to 4
  // note that, if we have currentExcludedResidentsNum, the number of excluded residents has changed and we need to get a new form
  const goingFrom3To4AsPartOfTransition =
    initialPipOption === '250000' &&
    currentPipOptionIndex === 4 &&
    initialExcludedResidents &&
    currentExcludedResidentsNum === 0;

  const addedDriverIds = Object.entries(addedChanges)
    .filter(([key, _]) => key.includes('drivers.') && key.includes('.id'))
    .map(([_, value]) => value);

  const removedDriverIds = Object.entries(removedChanges)
    .filter(([key, _]) => key.includes('drivers.') && key.includes('.id'))
    .map(([_, value]) => value);

  const driverAdded = addedDriverIds.some((id) => !removedDriverIds.includes(id));
  const driverRemoved = removedDriverIds.some((id) => !addedDriverIds.includes(id));

  const maritalStatusOfADriverChanged = Object.keys(removedChanges).some(
    (key) => key.includes('drivers.') && key.includes('maritalStatus') && addedChanges[key] !== removedChanges[key]
  );

  const primaryPersonChanged = Object.keys(addedChanges)
    .filter((key) => key.includes('drivers.') && key.includes('isPrimary'))
    .some((key) => addedChanges[key] === true && removedChanges[key] === false);

  const addedExcludedResident =
    addedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    removedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    currentExcludedResidentsNum > removedChanges['auto.pipExcludedResidents'];

  const removedExcludedResident =
    removedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    addedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    removedChanges['auto.pipExcludedResidents'] > currentExcludedResidentsNum;

  const pipExcludedResidentsChanged = addedExcludedResident || removedExcludedResident;

  const pipLimitChanged =
    addedChanges.hasOwnProperty('autoCoverage.policyLimitPIPME') &&
    removedChanges.hasOwnProperty('autoCoverage.policyLimitPIPME') &&
    currentPipOption !== removedChanges['autoCoverage.policyLimitPIPME'] &&
    // except when transitioning form old option 3 to new option 4
    !goingFrom3To4AsPartOfTransition;

  const addedHouseholdMember =
    addedChanges.hasOwnProperty('auto.pipAllResidents') &&
    removedChanges.hasOwnProperty('auto.pipAllResidents') &&
    addedChanges['auto.pipAllResidents'] > removedChanges['auto.pipAllResidents'];

  const removedHouseholdMember =
    removedChanges.hasOwnProperty('auto.pipAllResidents') &&
    addedChanges.hasOwnProperty('auto.pipAllResidents') &&
    removedChanges['auto.pipAllResidents'] > addedChanges['auto.pipAllResidents'];

  const conditionForProofOfElegibilityDocuments1 =
    [5, 6].includes(currentPipOptionIndex) &&
    (driverAdded || primaryPersonChanged || maritalStatusOfADriverChanged || addedHouseholdMember);

  // Proof-of-eligibility documents conditions
  const conditionForProofOfElegibilityDocuments2 =
    currentPipOptionIndex === 4 &&
    (primaryPersonChanged || maritalStatusOfADriverChanged || (pipExcludedResidentsChanged && addedExcludedResident));

  const conditionForProofOfElegibilityDocuments3 =
    currentPipOption &&
    // except when transitioning form old option 3 to new option 4
    !goingFrom3To4AsPartOfTransition &&
    [4, 5, 6].includes(getPipOptionIndex(currentPipOption, currentExcludedResidentsNum || currentExcludedResidentsNum));

  const showProofOfEligibilityDocumentsBlock =
    (conditionForProofOfElegibilityDocuments1 ||
      conditionForProofOfElegibilityDocuments2 ||
      conditionForProofOfElegibilityDocuments3) &&
    (!canApproveRequestedPIPChanges || requestedByIA);

  // All household residents included conditions
  const showAllHouseholdResidentsIncludedBlock =
    driverAdded || driverRemoved || removedHouseholdMember || addedHouseholdMember;

  // New PIP form required conditions
  const showNewPIPFormRequiredBlock = pipLimitChanged || (pipExcludedResidentsChanged && currentPipOptionIndex === 4);

  return {
    showProofOfEligibilityDocumentsBlock,
    showAllHouseholdResidentsIncludedBlock,
    showNewPIPFormRequiredBlock,
    hasChangesThatNeedApproval:
      showProofOfEligibilityDocumentsBlock || showAllHouseholdResidentsIncludedBlock || showNewPIPFormRequiredBlock
  };
};
